'use client';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { DirectContent } from '@/react/components/content/core/DirectContent';
import { useLocale } from '@/react/hooks/useLocale';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import type { IProductVM } from '@/react/view-models/ProductVM';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import type { Content } from '@/services/isomorphic/ContentService';
import type { IImage } from '@/services/models/Media/Image';
import { type IProduct } from '@/services/models/Product';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, Suspense, useState, useCallback } from 'react';
import { RecommendationContext } from '@/configs/recommendations';
import type { IVideo } from '@/services/models/Media/Video';
import { v4 as uuidv4 } from 'uuid';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import { ArtlabsExperienceButtons } from '@/react/components/Artlabs';
import { ExpressBuyWithApplePayButton } from '../../ApplePay';
import { AddToCartButton } from '../../cart/AddToCartButton';
import { Breakpoint, Breakpoints } from '../../core-ui/Breakpoints';
import { AboveATCDetails } from '../AboveATCDetails';
import { Link } from '../../core-ui/Link';
import { CommonErrorBoundary } from '../../errors/boundaries/CommonErrorBoundary';
import { QuickviewCarousel } from '../../product-list/quickview/QuickviewCarousel';
import { MainCarousel } from '../MainCarousel';
import ProductDetailsVariant from '../ProductDetailsVariant';
import { VariationPanel } from '../VariationPanel';
import PairsWith from '../../product-list/recommendation/PairsWith';
import { PDPGridMedia } from '../PDPGridLayout/components/PDPGridMedia';
import { Image } from '../../core-ui/Image';
import PromotionalMessages from '../PromotionalMessages';
import { BelowATCContent } from '../BelowATCContent';

import S from './styles.base.module.scss';
import { product_tiles_quickView_viewProductDetails } from "@/lang/__generated__/ahnu/product_tiles_quickView_viewProductDetails";

export interface IProductDetailsPanelProps extends PropsWithChildren {
  /** The product view model. */
  product: IProductVM;

  /**
   * The previously selected product that will be replaced on resolve.
   * For use with {@link variant} `=` {@link ProductDetailsVariant.Update}.
   */
  productToUpdate?: IProduct;

  /** Placement for the PDP Content. */
  belowDescriptionContent?: Content | null;

  /**
   * The variant of this details panel.
   * @see {@link ProductDetailsVariant} for more info on each variant.
   */
  variant?: ProductDetailsVariant;

  /**
   * Callback that will be executed when the variation selection is resolved.
   * @param newProduct - The new product variant that was just submitted.
   */
  onAddToCart?: (newProduct: IProduct) => void;
  /** The promotional messages for this product. */
  promotionalMessages?: Promise<Array<Content>>;

  /** Content to render above the CTA. */
  aboveAddToCartContent?: Content;
}

interface IProductDetailsPanelUpdateProps extends IProductDetailsPanelProps {
  /** @inheritdoc */
  productToUpdate: IProduct;

  /** @inheritdoc */
  variant: ProductDetailsVariant.Update;
}

/**
 * Props for the product details panel.
 */
type ProductDetailsPanelProps<T extends IProductDetailsPanelProps> =
  T['variant'] extends ProductDetailsVariant.Update
    ? IProductDetailsPanelUpdateProps & T
    : T['productToUpdate'] extends IProduct
      ? IProductDetailsPanelUpdateProps & T
      : T;

/** Can display a product's details in almost every setting. */
export const ProductDetailsPanel = observer(function ProductDetailsPanel<
  T extends IProductDetailsPanelProps
>({
  variant = ProductDetailsVariant.Default,
  product,
  productToUpdate,
  belowDescriptionContent,
  onAddToCart,
  promotionalMessages,
  aboveAddToCartContent,
  children
}: ProductDetailsPanelProps<T>) {
  const [msg] = useLocaleMessage();
  const { language, country } = useLocale();

  const { images, media } = product;

  // Size validation state
  const [showSizeError, setShowSizeError] = useState(false);

  const triggerSizeValidationError = useCallback(() => {
    setShowSizeError(true);
  }, []);

  const clearSizeValidationError = useCallback(() => {
    setShowSizeError(false);
  }, []);

  // If this panel is a quickview variant.
  const isQuickviewVariant =
    variant === ProductDetailsVariant.Quickview ||
    variant === ProductDetailsVariant.Update ||
    variant === ProductDetailsVariant.ExchangeQuickview;

  // If this panel is a full-size variant.
  const isFullSizeVariant =
    variant === ProductDetailsVariant.Default ||
    variant === ProductDetailsVariant.Exchange;

  const isGridVariant =
    isFullSizeVariant &&
    ConfigurationService.getConfig('product', language, country).getSetting(
      'displayImageGrid'
    ).value;

  // If the carousel should be passed into the variations panel on mobile.
  const isCarouselShownWithinVariationsPanelOnMobile =
    ConfigurationService.getConfig('product', language, country).getSetting(
      'isCarouselShownWithinVariationsPanelOnMobile'
    ).value;

  const productCarousel = (
    <CommonErrorBoundary>
      <div className={S.mediaContainer}>
        {!isNullOrEmpty(product.badgeImage) && (
          <div className={S.badgeContainer}>
            <Image
              image={{ uuid: uuidv4(), src: product.badgeImage, alt: 'Badge' }}
            />
          </div>
        )}
        {isFullSizeVariant ? (
          isGridVariant ? (
            <PDPGridMedia
              images={images as Array<IImage>}
              videos={media as Array<IVideo>}
            />
          ) : (
            <MainCarousel
              images={images as Array<IImage>}
              videos={media as Array<IVideo>}
            />
          )
        ) : (
          <QuickviewCarousel
            images={images as Array<IImage>}
            videos={media as Array<IVideo>}
          />
        )}
      </div>
    </CommonErrorBoundary>
  );

  // Access the product details from the product model.
  const productDetails = product.model.details;
  const aboveATCDetails =
    // Get the aboveATCDetails attribute from the product details, if available.
    typeof productDetails?.aboveCartDetails === 'string'
      ? productDetails.aboveCartDetails
      : null;

  // Whether or not to show the aboveCartDetails product attribute.
  // These are details that are displayed above the Add to Cart button.
  const showAboveATCDetails = ConfigurationService.getConfig(
    'product'
  ).getSetting('details.showAboveATCDetails').value;

  // Whether or not to show the custom content section below the 'Add to Cart' button.
  const showBelowATCContent = ConfigurationService.getConfig(
    'product'
  ).getSetting('details.showBelowATCContent').value;

  // Derive the product UPC for DY recommendation context data, consistent
  // with the identifier used in sendPageContext for page view events.
  const recommendationContextData = !isNullish(product.upc)
    ? [product.upc]
    : [];

  const bottomContent = (
    <>
      <ArtlabsExperienceButtons product={product} />

      {!isNullish(promotionalMessages) && (
        <PromotionalMessages messages={promotionalMessages} />
      )}
      {/* Whether or not to show the details above the Add to Cart button. */}
      {showAboveATCDetails && (
        <AboveATCDetails aboveCartDetails={aboveATCDetails} />
      )}
      {/* Content */}
      {!isNullish(aboveAddToCartContent) && (
        <DirectContent content={aboveAddToCartContent} />
      )}
      {/* The Add to Cart button. */}
      <div className={S.addToCartButton}>
        {/* TODO: This Suspense boundary is a temporary workaround to minimize the impact of
      an occasional hydration error which occurs when the VM updates with the availability before
      hydration has finished. */}
        <Suspense>
          <div>
            <AddToCartButton
              variant={variant}
              product={product}
              productToUpdate={productToUpdate}
              onAddToCart={onAddToCart}
              onSizeValidationError={triggerSizeValidationError}
              onClearSizeValidationError={clearSizeValidationError}
            />
          </div>
        </Suspense>
        {(variant === ProductDetailsVariant.Default ||
          variant === ProductDetailsVariant.Quickview) && (
          <ExpressBuyWithApplePayButton
            className={S.applePayContainer}
            product={product}
            theme="white-outline"
          />
        )}
      </div>
      {/* Custom content section below the 'Add to Cart' button. */}
      {showBelowATCContent && <BelowATCContent product={product} />}
      {/* Additional content injected from the client.
          Typically includes:
          - Short description (conditionally, if showShortDescription is true)
          - ProductDetailsAccordion (conditionally, if !displayProductDetails)
          - ValuePropositions (icon blocks for Locally, Free Shipping, Help Chat)
      */}
      {Boolean(children) && (
        <div className={S.additionalContentContainer}>{children}</div>
      )}
      {/**
       * Product "Pairs With" recommendations section.
       * Only show if the product has a SKU.
       */}
      <div className={S.tileBuy}>
        <PairsWith
          context={RecommendationContext.PairsWith}
          contextData={recommendationContextData}
        />
      </div>
      {/*
        Only show the "View Product Details" link if the variant
        is one of the quickview variants.
      */}
      {isQuickviewVariant && (
        <div className={S.fullDetails}>
          <Link href={product.urlWithCategory} variant="underline">
            {msg(product_tiles_quickView_viewProductDetails)}
          </Link>
        </div>
      )}
    </>
  );

  return (
    <div
      className={classes(
        S.productDetailsPanel,
        isQuickviewVariant && S.quickview
      )}
    >
      <Breakpoints>
        {isCarouselShownWithinVariationsPanelOnMobile && (
          <Breakpoint media="xxs:sm">
            <div className={S.variationPanelContainer}>
              {/*
                On mobile, we pass the product carousel as a child of
                the variation panel, so that it can be shown below
                the product selection options.
              */}

              <VariationPanel
                variant={variant}
                product={product}
                showSizeError={showSizeError}
                onClearSizeValidationError={clearSizeValidationError}
              >
                <div className={S.carouselContainer}>{productCarousel}</div>
              </VariationPanel>
              {bottomContent}
              {belowDescriptionContent && (
                <DirectContent content={belowDescriptionContent} />
              )}
            </div>
          </Breakpoint>
        )}
        <Breakpoint default>
          <>
            <div className={S.carouselContainer}>
              {productCarousel}
              {belowDescriptionContent && (
                <DirectContent content={belowDescriptionContent} />
              )}
            </div>
            <div className={S.variationPanelContainer}>
              <div className={S.variationPanelInnerContainer}>
                <VariationPanel
                  variant={variant}
                  product={product}
                  showSizeError={showSizeError}
                  onClearSizeValidationError={clearSizeValidationError}
                />
                <div className={S.bottomContent}>{bottomContent}</div>
              </div>
            </div>
          </>
        </Breakpoint>
      </Breakpoints>
    </div>
  );
});
